import React, { forwardRef } from 'react'
import { Pressable, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { radiobuttonContract } from './radiobutton.contract'

export const RADIO_LABEL_VARIANT = variantForStyleId(radiobuttonContract.textStyleIds[0])

type ContractCell = {
    container: string
    label: string
    disabled: string
    parts: Record<string, string>
}

const cellFor = (selected: boolean): ContractCell => {
    const key = `selected=${selected}`
    const found = (radiobuttonContract.variants as Record<string, ContractCell>)[key]
    if (!found) {
        throw new Error(`aos-ds RadioButton: the Figma contract has no "${key}"`)
    }
    return found
}

export type RadioButtonProps = Omit<
    React.ComponentProps<typeof Pressable>,
    'style' | 'children'
> & {
    /** Figma's `showLabel` and `labelText` in one: the label is drawn when there is one. */
    children?: string
    selected?: boolean
    className?: string
}

type RadioRef = React.ComponentRef<typeof Pressable>

/**
 * A ring with a dot in it, and a label beside them.
 *
 * The whole visual is delegated: Figma draws the ring as an instance of
 * `RadioButtonSelected` / `RadioButtonUnselected`, standalone components with
 * no states of their own. The hover colour exists only as an override applied
 * *inside* that instance by the parent's `hover=true` variant — so a dumper
 * that stops at an instance, as this one did, reported six identical variants
 * with no paint at all, and the component was filed as blocked on "instance
 * delegation" rather than on anything measurable.
 *
 * `radio frame` and `radio fill` are the names Figma gives the two ellipses.
 * The dot exists only in the selected cell, which is what makes it the dot.
 */
export const RadioButton = forwardRef<RadioRef, RadioButtonProps>(
    ({ children, selected = false, disabled = false, className, ...rest }, ref) => {
        const cell = cellFor(selected)
        return (
            <Pressable
                ref={ref}
                disabled={disabled}
                role='radio'
                accessibilityState={{ checked: selected, disabled: disabled ?? false }}
                className={twMerge(
                    'flex-row',
                    cell.container,
                    disabled ? cell.disabled : undefined,
                    className,
                )}
                testID={`radio-${selected ? 'selected' : 'unselected'}`}
                {...rest}
            >
                <View
                    className={twMerge('items-center justify-center', cell.parts['radio frame'])}
                    testID='radio-frame'
                >
                    {cell.parts['radio fill'] ? (
                        <View className={cell.parts['radio fill']} testID='radio-fill' />
                    ) : null}
                </View>
                {children ? (
                    <Typography variant={RADIO_LABEL_VARIANT} className={cell.parts.label}>
                        {children}
                    </Typography>
                ) : null}
            </Pressable>
        )
    },
)

RadioButton.displayName = 'RadioButton'
